import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail } from "lucide-react";
import { LOGO_URL } from "../../data/products";
import { useContent } from "../../lib/content";

const COLUMNS = [
  {
    heading: "Shop",
    links: [
      { to: "/shop?cat=halter+neck", label: "Halter Neck" },
      { to: "/shop?cat=corset+back", label: "Corset Back" },
      { to: "/shop?cat=floral-straps", label: "Floral Straps" },
      { to: "/shop", label: "All Kurtis" },
    ],
  },
  {
    heading: "Info",
    links: [
      { to: "/about", label: "Our Story" },
      { to: "/contact", label: "Contact" },
      { to: "/shop", label: "Size Guide" },
      { to: "/contact", label: "Shipping" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { to: "/legal/privacy", label: "Privacy Policy" },
      { to: "/legal/terms", label: "Terms & Conditions" },
      { to: "/legal/refund", label: "Refund & Returns" },
    ],
  },
];

const SOCIAL_ORDER = [
  { key: "instagram", icon: Instagram, label: "Instagram" },
  { key: "facebook", icon: Facebook, label: "Facebook" },
  { key: "email_url", icon: Mail, label: "Email" },
];

export default function Footer() {
  const { content } = useContent();
  const footer = content?.footer || {};
  const social = content?.social || {};
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="relative mt-24 pt-20 pb-10 bg-gradient-to-b from-cream-100 via-blush-50 to-cream-100 border-t border-blush-100 overflow-hidden"
    >
      <div className="blob bg-blush-200 w-[420px] h-[420px] -top-32 -right-24" />
      <div className="blob bg-blush-100 w-[360px] h-[360px] -bottom-40 -left-24" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Flownté"
              className="h-14 w-14 rounded-full object-cover ring-1 ring-blush-200"
            />
            <div className="flex flex-col leading-none">
              <span className="font-script text-4xl text-blush-600">Flownté</span>
              <span className="text-[10px] tracking-[0.32em] text-ink-500 uppercase">
                Elegance in Motion
              </span>
            </div>
          </div>
          <p className="mt-6 text-ink-700 font-body font-light leading-relaxed max-w-sm">
            {footer.tagline}
          </p>

          <div className="mt-6 flex items-center gap-3">
            {SOCIAL_ORDER.map((s) => social[s.key] ? (
              <a
                key={s.key}
                href={social[s.key]}
                target="_blank"
                rel="noreferrer"
                data-testid={`footer-social-${s.label.toLowerCase()}`}
                className="h-10 w-10 rounded-full grid place-items-center bg-white border border-blush-100 text-blush-600 hover:bg-blush-200 hover:border-blush-300 transition"
                aria-label={s.label}
              >
                <s.icon size={16} strokeWidth={1.5} />
              </a>
            ) : null)}
          </div>
        </div>

        {COLUMNS.map((col) => (
          <div key={col.heading} className="md:col-span-2">
            <h4 className="font-body text-xs uppercase tracking-[0.28em] text-blush-600 mb-5">
              {col.heading}
            </h4>
            <ul className="space-y-3">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm font-body text-ink-700 hover:text-blush-600 transition"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="md:col-span-2">
          <h4 className="font-body text-xs uppercase tracking-[0.28em] text-blush-600 mb-5">
            Contact
          </h4>
          <p className="text-sm font-body text-ink-700 leading-relaxed whitespace-pre-line">
            {footer.contact_block}
          </p>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 mt-16 pt-8 border-t border-blush-100 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-xs font-body tracking-[0.18em] uppercase text-ink-500">
          {(footer.copyright || "").replace("{year}", year)}
        </p>
        <p className="text-xs font-body text-ink-500">
          {footer.sub_copyright}
        </p>
      </div>
    </footer>
  );
}
